<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';

import ContactAPI from 'dashboard/api/contacts';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  stages: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['create']);

const { t } = useI18n();
const dialogRef = ref(null);

const form = reactive({
  stageId: null,
  contact: null,
  estimatedValue: '',
});

const searchQuery = ref('');
const searchResults = ref([]);
const isSearching = ref(false);
const hasSearched = ref(false);

const isFormInvalid = computed(() => !form.contact || !form.stageId);

const searchContacts = useDebounceFn(async () => {
  const query = searchQuery.value.trim();
  if (query.length < 2) {
    searchResults.value = [];
    hasSearched.value = false;
    return;
  }

  isSearching.value = true;
  try {
    const { data } = await ContactAPI.search(query);
    searchResults.value = data.payload || [];
    hasSearched.value = true;
  } finally {
    isSearching.value = false;
  }
}, 300);

const onSearchInput = () => {
  form.contact = null;
  searchContacts();
};

const selectContact = contact => {
  form.contact = contact;
  searchQuery.value = contact.name;
  searchResults.value = [];
  hasSearched.value = false;
};

const resetForm = () => {
  form.stageId = null;
  form.contact = null;
  form.estimatedValue = '';
  searchQuery.value = '';
  searchResults.value = [];
  hasSearched.value = false;
};

// Abre o modal; quando chamado pelo "+" de uma coluna, pré-seleciona o estágio.
const open = (stageId = null) => {
  resetForm();
  form.stageId = stageId || props.stages[0]?.id || null;
  dialogRef.value?.open();
};

const handleConfirm = () => {
  if (isFormInvalid.value) return;

  emit('create', {
    pipeline_stage_id: form.stageId,
    contact_id: form.contact.id,
    estimated_value: form.estimatedValue
      ? Number(form.estimatedValue)
      : null,
  });
};

const closeDialog = () => {
  dialogRef.value?.close();
};

const onSuccess = () => {
  resetForm();
  closeDialog();
};

defineExpose({ open, onSuccess });
</script>

<template>
  <Dialog ref="dialogRef" width="xl" @confirm="handleConfirm" @close="resetForm">
    <div class="flex flex-col gap-5">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('PIPELINE.ADD_CARD.TITLE') }}
      </span>

      <div class="relative flex flex-col gap-1.5">
        <label class="text-sm text-n-slate-11">
          {{ t('PIPELINE.ADD_CARD.CONTACT_LABEL') }}
        </label>
        <Input
          v-model="searchQuery"
          :placeholder="t('PIPELINE.ADD_CARD.CONTACT_PLACEHOLDER')"
          :disabled="isLoading"
          autofocus
          @input="onSearchInput"
        />
        <div
          v-if="isSearching"
          class="flex items-center gap-2 px-1 text-xs text-n-slate-11"
        >
          <Spinner class="size-3" />
          {{ t('PIPELINE.ADD_CARD.SEARCHING') }}
        </div>
        <ul
          v-else-if="searchResults.length"
          class="absolute z-10 w-full mt-1 overflow-y-auto bg-white border rounded-lg shadow-lg top-full dark:bg-n-solid-2 border-n-weak max-h-52"
        >
          <li v-for="contact in searchResults" :key="contact.id">
            <button
              class="flex flex-col w-full gap-0.5 px-3 py-2 text-left hover:bg-n-slate-3"
              @click="selectContact(contact)"
            >
              <span class="text-sm text-n-slate-12">{{ contact.name }}</span>
              <span
                v-if="contact.phone_number || contact.email"
                class="text-xs text-n-slate-11"
              >
                {{ contact.phone_number || contact.email }}
              </span>
            </button>
          </li>
        </ul>
        <span
          v-else-if="hasSearched && !form.contact"
          class="px-1 text-xs text-n-slate-11"
        >
          {{ t('PIPELINE.ADD_CARD.NO_RESULTS') }}
        </span>
      </div>

      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div class="flex flex-col gap-1.5">
          <label class="text-sm text-n-slate-11">
            {{ t('PIPELINE.ADD_CARD.STAGE_LABEL') }}
          </label>
          <select
            v-model="form.stageId"
            :disabled="isLoading"
            class="w-full px-3 py-2 text-sm border rounded-lg outline-none bg-n-solid-1 border-n-weak text-n-slate-12"
          >
            <option v-for="stage in stages" :key="stage.id" :value="stage.id">
              {{ stage.name }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm text-n-slate-11">
            {{ t('PIPELINE.ADD_CARD.VALUE_LABEL') }}
          </label>
          <Input
            v-model="form.estimatedValue"
            type="number"
            min="0"
            step="0.01"
            :placeholder="t('PIPELINE.ADD_CARD.VALUE_PLACEHOLDER')"
            :disabled="isLoading"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="link"
          type="reset"
          class="h-10 hover:!no-underline hover:text-n-brand"
          @click="closeDialog"
        />
        <Button
          :label="t('PIPELINE.ADD_CARD.SAVE')"
          color="blue"
          type="submit"
          :disabled="isFormInvalid || isLoading"
          :is-loading="isLoading"
        />
      </div>
    </template>
  </Dialog>
</template>
